import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LinkOutlined } from '@ant-design/icons'
import { isNil, map, pick, uniq } from 'lodash'
import PropTypes from 'prop-types'

import { showErrorNotification } from '../../../core/helpers'
import { useQueryParams } from '../../../core/hooks'
import { areSameValue } from '../../../core/utils'

import { PAGINATION } from '../../../common/constants'
import { findNodeByFilter } from '../../../common/utils'

import { MENU_CURATION_TASK_ENTITY_LINK_ORIGIN_TYPE } from '../../../menu-curation-tasks/constants'
import {
  setMenuTitleExpandedRowKeys,
  setMenuTitleHighlightedRowKey,
} from '../../../menu-title/redux/actions'
import { UNKNOWN_WORDS_ORIGIN_FIELD_NAMES } from '../../constants'
import {
  useConfirmUnknownWordMutation,
  useDeleteNotExportedUnknownWordMutation,
  useUnconfirmUnknownWordMutation,
  useUserHasUnknownWordChangePermission,
} from '../../hooks'
import { unknownWordPropType } from '../../prop-types'
import { transformUnknownWordFormValuesToUpdateArgs } from '../../transformers'
import { UnknownWordForm } from '../forms'
import { StyledUnknownWordsCard } from '../styled'

import UnknownWordsTimestamps from './unknown-words-timestamps'

const UnknownWordCard = ({ onClose, unknownWord }) => {
  const dispatch = useDispatch()

  const { data: menuCurationTask, menuTitleExpandedRowKeys: expandedRowKeys } =
    useSelector(store => store.menuCurationTasks.menuCurationTask)

  const { menuTitles } = menuCurationTask?.menu || {}
  const { isUnknownWordsCurationDone, isUnknownWordsQaDone } = menuCurationTask
  const { setQueryParams } = useQueryParams()

  const { userHasChangePermission } = useUserHasUnknownWordChangePermission(
    menuCurationTask,
    unknownWord,
  )

  const [confirm, { isLoading: isConfirmLoading, error: confirmError }] =
    useConfirmUnknownWordMutation(unknownWord.id)

  const [unconfirm] = useUnconfirmUnknownWordMutation(unknownWord.id)

  const [
    deleteNotExportedWord,
    { isLoading: isDeleteNotExportedWordLoading, error: deleteError },
  ] = useDeleteNotExportedUnknownWordMutation(unknownWord.id)

  useEffect(() => {
    if (confirmError || deleteError) {
      showErrorNotification({
        title: 'Failed to update unknown word',
        description: confirmError || deleteError,
      })
    }
  }, [confirmError, deleteError])

  const timestamps = useMemo(
    () => pick(unknownWord, ['curatedAt', 'curatedBy', 'qadAt', 'qadBy']),
    [unknownWord],
  )

  const isDisabled = useMemo(() => {
    if (!userHasChangePermission) {
      return true
    }

    if (unknownWord.isExported) {
      return true
    }

    return false
  }, [unknownWord.isExported, userHasChangePermission])

  const wordOriginField = useMemo(
    () => UNKNOWN_WORDS_ORIGIN_FIELD_NAMES[unknownWord.originField],
    [unknownWord.originField],
  )

  const originLink = useMemo(() => {
    if (!isNil(unknownWord.dish) && !unknownWord.dish.isDeleted) {
      return {
        idToOpen: unknownWord.dish.id,
        type: MENU_CURATION_TASK_ENTITY_LINK_ORIGIN_TYPE.DISH,
      }
    }

    if (!isNil(unknownWord.menuTitle) && !unknownWord.menuTitle.isDeleted) {
      return {
        idToOpen: unknownWord.menuTitle?.id,
        type: MENU_CURATION_TASK_ENTITY_LINK_ORIGIN_TYPE.MENU_TITLE,
      }
    }

    return null
  }, [unknownWord.dish, unknownWord.menuTitle])

  const handleHighlightMenuTitle = useCallback(() => {
    const menuTitleToOpen = findNodeByFilter(menuTitles, ({ id }) =>
      areSameValue(id, originLink?.idToOpen),
    )

    if (!isNil(menuTitleToOpen)) {
      dispatch(
        setMenuTitleExpandedRowKeys(
          uniq([
            ...expandedRowKeys,
            ...map(
              [...menuTitleToOpen.idPath, menuTitleToOpen.id],
              menuTitleId => `menuTitle-${menuTitleId}`,
            ),
          ]),
        ),
      )
      dispatch(setMenuTitleHighlightedRowKey(`menuTitle-${menuTitleToOpen.id}`))
      onClose()
    }
  }, [dispatch, expandedRowKeys, menuTitles, onClose, originLink?.idToOpen])

  const handleHighlightDish = useCallback(() => {
    if (!isNil(originLink)) {
      setQueryParams({
        isUnknownWordsModalShown: null,
        unknownWordsCurrentPage: null,
        unknownWordsPageSize: null,
        sortUnknownWordsBy: null,
        dishIds: [originLink.idToOpen],
        shouldScrollToDish: true,
        currentPage: PAGINATION.DEFAULT_PAGE,
      })
    }
  }, [originLink, setQueryParams])

  const cardExtra = useMemo(() => {
    if (isNil(originLink)) {
      return null
    }

    return originLink.type ===
      MENU_CURATION_TASK_ENTITY_LINK_ORIGIN_TYPE.DISH ? (
      <LinkOutlined onClick={handleHighlightDish} />
    ) : (
      <LinkOutlined onClick={handleHighlightMenuTitle} />
    )
  }, [handleHighlightDish, handleHighlightMenuTitle, originLink])

  const handleConfirm = useCallback(
    formValues =>
      confirm(transformUnknownWordFormValuesToUpdateArgs(formValues)),
    [confirm],
  )

  const handleDelete = useCallback(
    () => deleteNotExportedWord(),
    [deleteNotExportedWord],
  )

  const handleUnconfirm = useCallback(() => unconfirm(), [unconfirm])

  return (
    <StyledUnknownWordsCard
      loading={isConfirmLoading || isDeleteNotExportedWordLoading}
      extra={cardExtra}
      type="inner"
      title={wordOriginField}
      disabled={isDisabled}
      headStyle={{ borderBottom: 'none' }}
      bodyStyle={{ padding: '0.75rem' }}
    >
      <UnknownWordForm
        isDisabled={isDisabled}
        unknownWord={unknownWord}
        onDelete={handleDelete}
        onConfirm={handleConfirm}
        onUnconfirm={handleUnconfirm}
        isUnknownWordsCurationDone={isUnknownWordsCurationDone}
        isUnknownWordsQaDone={isUnknownWordsQaDone}
      />
      <UnknownWordsTimestamps timestamps={timestamps} />
    </StyledUnknownWordsCard>
  )
}

UnknownWordCard.propTypes = {
  onClose: PropTypes.func.isRequired,
  unknownWord: unknownWordPropType.isRequired,
}

export default UnknownWordCard
